import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard,
  Users,
  CalendarDays,
  CalendarClock,
  Files,
  KanbanSquare,
  Clock,
  Bell,
  Shield,
  LogOut,
  MessageSquare,
  Menu,
  X,
} from "lucide-react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { NAV } from "@/constants/ui";
import { AvatarBubble } from "@/components/AvatarUploader";

const baseLinks = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: NAV.dashboard, mobile: true },
  { to: "/mitarbeiter", label: "Mitarbeiter", icon: Users, adminOnly: true, testid: NAV.employees, mobile: true },
  { to: "/urlaub", label: "Urlaub", icon: CalendarDays, testid: NAV.vacation, mobile: true },
  { to: "/termine", label: "Termine", icon: CalendarClock, testid: NAV.appointments, mobile: false },
  { to: "/dokumente", label: "Dokumente", icon: Files, testid: NAV.documents, mobile: true },
  { to: "/aufgaben", label: "Aufgaben", icon: KanbanSquare, testid: NAV.todos, mobile: true },
  { to: "/zeiterfassung", label: "Zeiterfassung", icon: Clock, testid: NAV.time, mobile: false },
  { to: "/feedback", label: "Rückmeldungen", icon: MessageSquare, testid: NAV.feedback, mobile: false },
  { to: "/benachrichtigungen", label: "Mitteilungen", icon: Bell, testid: NAV.notifications, mobile: false },
  { to: "/audit-log", label: "Audit-Log", icon: Shield, adminOnly: true, testid: NAV.audit, mobile: false },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [drawer, setDrawer] = useState(false);

  useEffect(() => setDrawer(false), [location.pathname]);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/dashboard/summary");
        if (alive) setUnread(data.unread_notifications || 0);
      } catch (e) { void e; }
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [user]);

  if (!user) return null;
  const links = baseLinks.filter((l) => !l.adminOnly || user.role === "admin");
  const mobileLinks = links.filter((l) => l.mobile);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-30 flex items-center justify-between px-4 h-14 bg-kauz-navy text-white border-b border-kauz-navy-dark">
        <Link to="/" className="flex items-center gap-2">
          <Logo />
          <span className="font-display text-base font-semibold tracking-tight">Kauz Logistik</span>
        </Link>
        <div className="flex items-center gap-1">
          <button
            data-testid="mobile-notifications"
            onClick={() => navigate("/benachrichtigungen")}
            className="relative h-9 w-9 grid place-items-center rounded-md hover:bg-kauz-navy-dark"
            aria-label="Benachrichtigungen"
          >
            <Bell className="h-5 w-5 text-slate-200" />
            {unread > 0 && (
              <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-kauz-gold" />
            )}
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setDrawer(true)}
            className="h-9 w-9 grid place-items-center rounded-md hover:bg-kauz-navy-dark"
            aria-label="Menü"
          >
            <Menu className="h-5 w-5 text-slate-200" />
          </button>
        </div>
      </header>

      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col fixed top-0 left-0 bottom-0 w-64 bg-kauz-navy text-slate-300 z-20">
        <div className="px-6 h-16 flex items-center gap-2.5 border-b border-kauz-navy-dark">
          <Logo />
          <span className="font-display text-lg font-semibold tracking-tight text-white">Kauz Logistik</span>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {links.map((l) => (
            <SideLink key={l.to} link={l} unread={l.to === "/benachrichtigungen" ? unread : 0} />
          ))}
        </nav>
        <div className="p-3 border-t border-kauz-navy-dark">
          <UserBlock user={user} onLogout={logout} />
        </div>
      </aside>

      {/* Mobile drawer */}
      {drawer && (
        <div className="md:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-slate-900/60" onClick={() => setDrawer(false)} />
          <aside className="absolute right-0 top-0 bottom-0 w-72 bg-kauz-navy text-slate-300 shadow-xl flex flex-col">
            <div className="h-14 flex items-center justify-between px-4 border-b border-kauz-navy-dark">
              <span className="font-display font-semibold text-white">Menü</span>
              <button onClick={() => setDrawer(false)} className="h-9 w-9 grid place-items-center rounded-md hover:bg-kauz-navy-dark text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex-1 px-3 py-3 space-y-0.5 overflow-y-auto">
              {links.map((l) => (
                <SideLink key={l.to} link={l} unread={l.to === "/benachrichtigungen" ? unread : 0} />
              ))}
            </nav>
            <div className="p-3 border-t border-kauz-navy-dark">
              <UserBlock user={user} onLogout={logout} />
            </div>
          </aside>
        </div>
      )}

      {/* Main */}
      <main className="md:ml-64 pb-24 md:pb-10">
        <div className="px-4 md:px-8 py-6 md:py-8 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-slate-200 grid grid-cols-5">
        {mobileLinks.slice(0, 5).map((l) => {
          const Icon = l.icon;
          return (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`bottom-${l.testid}`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center py-2 text-[11px] gap-0.5 ${
                  isActive ? "text-kauz-navy" : "text-slate-500"
                } active:scale-95 transition`
              }
            >
              <Icon className="h-5 w-5" strokeWidth={2} />
              <span className="leading-none">{l.label}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}

function SideLink({ link, unread }) {
  const Icon = link.icon;
  return (
    <NavLink
      to={link.to}
      end={link.end}
      data-testid={link.testid}
      className={({ isActive }) =>
        `group flex items-center gap-3 pl-3 pr-3 py-2.5 rounded-md text-sm font-medium border-l-2 transition ${
          isActive
            ? "bg-kauz-navy-dark text-white border-kauz-gold"
            : "text-slate-400 border-transparent hover:bg-kauz-navy-dark/60 hover:text-white"
        }`
      }
    >
      <Icon className="h-4.5 w-4.5" strokeWidth={2} />
      <span className="flex-1">{link.label}</span>
      {unread > 0 && (
        <Badge className="bg-kauz-gold hover:bg-kauz-gold text-white border-0 h-5 px-1.5 text-[10px]">
          {unread}
        </Badge>
      )}
    </NavLink>
  );
}

function UserBlock({ user, onLogout }) {
  return (
    <div className="flex items-center gap-3">
      <Link to="/profil" data-testid={NAV.profile} className="flex items-center gap-3 flex-1 min-w-0">
        <AvatarBubble user={user} size="sm" />
        <div className="min-w-0">
          <div className="text-sm font-medium text-white truncate">{user.name}</div>
          <div className="text-[11px] uppercase tracking-wider text-slate-400">
            {user.role === "admin" ? "Chef / Admin" : "Mitarbeiter"}
          </div>
        </div>
      </Link>
      <button
        data-testid={NAV.logout}
        onClick={onLogout}
        className="h-8 w-8 grid place-items-center rounded-md text-slate-400 hover:bg-kauz-navy-dark hover:text-white"
        title="Abmelden"
      >
        <LogOut className="h-4 w-4" />
      </button>
    </div>
  );
}

function Logo() {
  return (
    <div className="h-9 w-9 rounded-xl bg-white grid place-items-center shadow-[0_2px_10px_-2px_rgba(30,46,92,0.35)] ring-1 ring-white/10 p-1">
      <img src="/brand/kauz-icon.png" alt="Kauz Logistik" className="h-full w-auto" draggable="false" />
    </div>
  );
}
